import React from 'react';

import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import getTestProps from 'test/lib/cypress/getTestProps';
import Chip from 'ui-library/chips/Chip';
import * as ChipUtil from 'ui-library/chips/ChipUtil';
import {SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import NoAccessChip, {NoAccessChipProps} from 'ui-library/chips/NoAccessChip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {ChipItem, SmartChipType} from './useSmartChipQuery';

export interface SmartChipProps<T extends SmartChipType> extends SharedSmartChipProps<T> {
  item?: ChipItem<T>;
  isInaccessible?: boolean | null;
  onClick: (e: BasicEvent) => void;
  requestFetch?: (id: string) => void;
  icon: BenchlingIconName | null;
  popoverContentOverride?: JSX.Element | null;
  tooltipTitleOverride?: string | null;
  url: string | null;
  isReadonly?: boolean;
}

export default class SmartChip<T extends SmartChipType> extends React.PureComponent<SmartChipProps<T>> {
  static defaultProps = {isLink: true};

  override componentDidMount(): void {
    if (this.props.id && this.props.requestFetch) {
      this.props.requestFetch(this.props.id);
    }
  }

  override componentDidUpdate(): void {
    if (this.props.id && this.props.requestFetch) {
      this.props.requestFetch(this.props.id);
    }
  }

  override render(): JSX.Element {
    const {item, isInaccessible, url, icon, getSecondaryText, isReadonly, displayNameOverride, iconOverride} =
      this.props;
    const sharedChipProps = {
      className: this.props.className,
      negative: this.props.negative,
      size: this.props.size,
      onBlur: this.props.onBlur,
      onFocus: this.props.onFocus,
      onKeyDown: this.props.onKeyDown,
      onRemove: this.props.onRemove,
      tooltipTitleOverride: this.props.tooltipTitleOverride,
    };

    if (item) {
      // displayNameOverride is non-null when frozen chips are enabled, and overwrites the live-fetched displayLabel for the chip
      const text =
        displayNameOverride != null
          ? ArchivePurposesUtil.getArchiveItemName({...item, name: displayNameOverride})
          : 'displayLabel' in item
            ? ArchivePurposesUtil.getArchiveItemName({...item, name: item.displayLabel})
            : ArchivePurposesUtil.getArchiveItemName(item);

      return (
        <Chip
          {...sharedChipProps}
          faded={isArchived(item)}
          href={this.props.isLink ? url : null}
          icon={iconOverride ?? icon}
          isReadonly={isReadonly}
          onClick={this.props.isLink ? this.props.onClick : null}
          text={text}
          tooltipTitle={this.props.tooltipTitleOverride ?? ChipUtil.getTooltipTitle(item)}
          popoverContent={this.props.popoverContentOverride ?? ChipUtil.getPopoverContent(item)}
          secondaryText={getSecondaryText?.(item)}
        />
      );
    } else if (isInaccessible) {
      return <NoAccessChip {...(sharedChipProps as NoAccessChipProps)} />;
    } else {
      return (
        <Chip
          {...sharedChipProps}
          text={
            <span
              {...loadingIndicatorAttrs()}
              {...getTestProps({component: 'SmartChip', element: 'loadingIndicator'})}
            >
              Loading...
            </span>
          }
        />
      );
    }
  }
}
